// Intent: powers the "pull down on Start -> Podsumowanie + przełącznik okresów"
// gesture (owner request). Sums money the user already entered over a calendar
// period (this month / this quarter / this year). Pure calendar math — nothing
// is guessed; empty data => zeros.

export type SummaryPeriod = 'month' | 'quarter' | 'year';

export interface PeriodSummary {
  incomeGr: number;
  expenseGr: number;
  balanceGr: number;
  savedGr: number;
}

/** Calendar range for the period containing `today`, plus the 'YYYY-MM' keys it spans. */
function periodRange(period: SummaryPeriod, today: Date): { start: Date; end: Date; monthKeys: string[] } {
  const y = today.getFullYear();
  const m = today.getMonth(); // 0-11
  let startMonth = m;
  let endMonth = m;
  if (period === 'quarter') {
    startMonth = Math.floor(m / 3) * 3;
    endMonth = startMonth + 2;
  } else if (period === 'year') {
    startMonth = 0;
    endMonth = 11;
  }
  const start = new Date(y, startMonth, 1, 0, 0, 0, 0);
  const end = new Date(y, endMonth + 1, 0, 23, 59, 59, 999); // day 0 of next month = last day
  const monthKeys: string[] = [];
  for (let mm = startMonth; mm <= endMonth; mm++) {
    monthKeys.push(`${y}-${String(mm + 1).padStart(2, '0')}`);
  }
  return { start, end, monthKeys };
}

function inRange(iso: string, start: Date, end: Date): boolean {
  const d = new Date(iso);
  return !Number.isNaN(d.getTime()) && d >= start && d <= end;
}

/**
 * Income comes from monthly income entries (keyed by 'YYYY-MM'); expenses and
 * savings come from timestamped rows (createdAt). Balance = income - expenses.
 */
export function periodSummaryOf(
  period: SummaryPeriod,
  today: Date,
  income: { month: string; amountGr: number }[],
  expenses: { createdAt: string; amountGr: number }[],
  contributions: { createdAt: string; amountGr: number }[],
): PeriodSummary {
  const { start, end, monthKeys } = periodRange(period, today);
  const incomeGr = income
    .filter((e) => monthKeys.includes(e.month))
    .reduce((s, e) => s + e.amountGr, 0);
  const expenseGr = expenses
    .filter((e) => inRange(e.createdAt, start, end))
    .reduce((s, e) => s + e.amountGr, 0);
  const savedGr = contributions
    .filter((c) => inRange(c.createdAt, start, end))
    .reduce((s, c) => s + c.amountGr, 0);
  return { incomeGr, expenseGr, balanceGr: incomeGr - expenseGr, savedGr };
}
